/**
 * Taxonomy tag editor modal — create or edit a system default tag.
 *
 * Surface for TAX-FN-002 (create) and TAX-FN-003 (update) in TAX_SPEC.md.
 * TAX-FN-004 (retire) is exposed via the Delete button on edit mode.
 *
 * @copyright 2025 Glimbr Pty Ltd
 * @version 2026-04-24-v1
 */
import { useEffect, useState } from 'react';
import { Modal } from '../components/modal';
import { RoleButton } from '../components/role-gate';
import { createTaxonomyTag, deleteTaxonomyTag, updateTaxonomyTag } from '../../lib/api';
import { getHttpErrorMessage } from '../../lib/http';
import type { TaxonomyCategorySlug, TaxonomyTag } from '../../lib/types';

const CATEGORY_OPTIONS: { value: TaxonomyCategorySlug; label: string }[] = [
    { value: 'genre', label: 'Genre' },
    { value: 'mood', label: 'Mood' },
    { value: 'instrumentation', label: 'Instrumentation' },
    { value: 'purpose', label: 'Purpose / Use Case' },
    { value: 'status', label: 'Status' },
    { value: 'format', label: 'Format' }
];

// Must match COLOR_HEX_BY_VAR in src/admin/taxonomy.js
const COLOR_PALETTE: { var: string; hex: string; label: string }[] = [
    { var: '--primary', hex: '#3B82F6', label: 'Blue' },
    { var: '--purple', hex: '#A855F7', label: 'Purple' },
    { var: '--cyan', hex: '#06B6D4', label: 'Cyan' },
    { var: '--success', hex: '#22C55E', label: 'Green' },
    { var: '--warning', hex: '#F59E0B', label: 'Amber' },
    { var: '--danger', hex: '#EF4444', label: 'Red' },
    { var: '--muted', hex: '#94A3B8', label: 'Grey' },
    { var: '--text-muted', hex: '#64748B', label: 'Slate' },
    { var: '--text-dim', hex: '#475569', label: 'Dim slate' }
];

export interface TaxonomyTagEditorProps {
    open: boolean;
    mode: 'create' | 'edit';
    tag?: TaxonomyTag | null;
    /** Prefill category on create, read-only on edit unless user changes it. */
    defaultCategory?: TaxonomyCategorySlug | null;
    onClose: () => void;
    /** Called after a successful save/delete so the page can refetch. */
    onSaved: () => void;
}

export function TaxonomyTagEditorModal({
    open,
    mode,
    tag,
    defaultCategory,
    onClose,
    onSaved
}: TaxonomyTagEditorProps) {
    const [name, setName] = useState('');
    const [category, setCategory] = useState<TaxonomyCategorySlug>('genre');
    const [colorVar, setColorVar] = useState<string>('--primary');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [confirmDelete, setConfirmDelete] = useState(false);

    useEffect(() => {
        if (!open) return;
        setError(null);
        setConfirmDelete(false);
        if (mode === 'edit' && tag) {
            setName(tag.name);
            setCategory(tag.category);
            setColorVar(tag.color_var || '--primary');
        } else {
            setName('');
            setCategory(defaultCategory || 'genre');
            setColorVar('--primary');
        }
    }, [open, mode, tag, defaultCategory]);

    const handleSave = async () => {
        const trimmed = name.trim();
        if (!trimmed) {
            setError('Tag name is required');
            return;
        }
        setSubmitting(true);
        setError(null);
        try {
            if (mode === 'create') {
                await createTaxonomyTag({ name: trimmed, category, color_var: colorVar });
            } else if (tag) {
                await updateTaxonomyTag(tag.id, { name: trimmed, category, color_var: colorVar });
            }
            onSaved();
            onClose();
        } catch (err) {
            setError(getHttpErrorMessage(err, 'Failed to save tag'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async () => {
        if (!tag) return;
        if (!confirmDelete) {
            setConfirmDelete(true);
            return;
        }
        setSubmitting(true);
        setError(null);
        try {
            await deleteTaxonomyTag(tag.id);
            onSaved();
            onClose();
        } catch (err) {
            setError(getHttpErrorMessage(err, 'Failed to delete tag'));
            setSubmitting(false);
        }
    };

    const title = mode === 'create' ? 'Add default tag' : `Edit tag · ${tag?.name ?? ''}`;
    const subtitle =
        mode === 'create'
            ? 'Adds a new system/default tag. Every user will see it on their next fetch.'
            : 'Updates this system tag. Users who customised its colour will have their override preserved per user on the main tags API — this form writes the new default.';

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={title}
            subtitle={subtitle}
            width={520}
            footer={
                <>
                    {mode === 'edit' ? (
                        <RoleButton
                            scope="owner_only"
                            className={`btn ${confirmDelete ? 'btn-danger' : 'btn-ghost'}`}
                            onClick={handleDelete}
                            disabled={submitting}
                            title={
                                confirmDelete
                                    ? 'Click again to confirm permanent deletion'
                                    : 'Retire this default tag'
                            }
                        >
                            {confirmDelete ? 'Click to confirm delete' : 'Delete tag'}
                        </RoleButton>
                    ) : (
                        <span />
                    )}
                    <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
                        <button className="btn btn-ghost" onClick={onClose} disabled={submitting}>
                            Cancel
                        </button>
                        <RoleButton
                            scope="owner_only"
                            className="btn btn-primary"
                            onClick={handleSave}
                            disabled={submitting || !name.trim()}
                        >
                            {submitting
                                ? 'Saving…'
                                : mode === 'create'
                                  ? 'Create tag'
                                  : 'Save changes'}
                        </RoleButton>
                    </div>
                </>
            }
        >
            <div className="form-field" style={{ marginBottom: 16 }}>
                <label className="form-label" htmlFor="tax-tag-name">
                    Name
                </label>
                <input
                    id="tax-tag-name"
                    type="text"
                    className="form-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Country, Slow, Male Vocal"
                    maxLength={80}
                    autoFocus
                />
            </div>

            <div className="form-field" style={{ marginBottom: 16 }}>
                <label className="form-label" htmlFor="tax-tag-category">
                    Category
                </label>
                <select
                    id="tax-tag-category"
                    className="form-input"
                    value={category}
                    onChange={(e) => setCategory(e.target.value as TaxonomyCategorySlug)}
                >
                    {CATEGORY_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                            {opt.label}
                        </option>
                    ))}
                </select>
            </div>

            <div className="form-field" style={{ marginBottom: 16 }}>
                <span className="form-label">Colour</span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                    {COLOR_PALETTE.map((c) => {
                        const selected = c.var === colorVar;
                        return (
                            <button
                                key={c.var}
                                type="button"
                                onClick={() => setColorVar(c.var)}
                                title={c.label}
                                style={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: 6,
                                    border: selected
                                        ? `2px solid ${c.hex}`
                                        : '1px solid var(--border)',
                                    background: `color-mix(in oklab, ${c.hex} 18%, transparent)`,
                                    color: c.hex,
                                    cursor: 'pointer',
                                    position: 'relative',
                                    padding: 0
                                }}
                            >
                                <span
                                    style={{
                                        display: 'block',
                                        width: 14,
                                        height: 14,
                                        borderRadius: '50%',
                                        background: c.hex,
                                        margin: '0 auto'
                                    }}
                                />
                            </button>
                        );
                    })}
                </div>
            </div>

            {error && (
                <div style={{ marginTop: 12, fontSize: 12, color: 'var(--danger)' }}>{error}</div>
            )}
        </Modal>
    );
}
